import { ReactNode } from 'react';
import { useAppTranslate } from '@src/i18n/i18n';
import { useCheckResponse } from '@src/hooks';
import { Modal } from '../Modal';
import { Button } from 'antd';
import { TrashIcon } from '@heroicons/react/24/solid';
import { CheckIcon } from '@heroicons/react/16/solid';
import { IoCheckmark } from 'react-icons/io5';

type DeleteModalProps = {
    pageTitle?: string;
    itemName?: string;
    isOpen: boolean;
    isDeleting?: boolean;
    deleteMessage?: ReactNode;
    onClose: () => void;
    onDelete: () => Promise<void>;
    icon?: ReactNode;
};

export function DeleteModal({
    isOpen,
    isDeleting = false,
    deleteMessage,
    onClose,
    onDelete,
    pageTitle,
    icon,
    itemName,
}: Readonly<DeleteModalProps>) {
    const { t } = useAppTranslate();
    const { checkResponse } = useCheckResponse();
    const handleCancel = () => {
        onClose();
    };

    const handleDelete = async () => {
        try {
            await onDelete();
        } catch (error) {
            checkResponse(error);
        } finally {
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            title={`${t('general.delete')} ${pageTitle ? pageTitle : ''}`}
            headerIcon={<TrashIcon width={24} height={24} />}
            onClose={handleCancel}
            content={
                <div className="flex flex-col justify-evenly flex-1 h-20">
                    <p className="text-base">
                        <span className=" font-normal leading-normal">
                            {t('keyTerms.deleteMessage', {
                                name: pageTitle,
                            })}
                        </span>
                    </p>
                    <div className="flex items-center gap-1 font-medium text-xl">
                        {icon}
                        <span>{itemName}</span>
                    </div>
                </div>
            }
            footer={
                <div className="flex gap-4">
                    <Button
                        size="large"
                        className="btn-secondary w-1/2"
                        onClick={handleCancel}
                    >
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        size="large"
                        className="btn-danger w-1/2"
                        loading={isDeleting}
                        onClick={handleDelete}
                        icon={<IoCheckmark />}
                    >
                        {t('buttons.confirmDelete')}
                    </Button>
                </div>
            }
        />
    );
}
